import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

// NOTE: This component should be rendered globally (e.g., in App.js) to prevent re-mounting across pages
// and ensure consistent banner behavior throughout the application.

const COOLDOWN_HOURS = 24;
const COOLDOWN_MS = COOLDOWN_HOURS * 60 * 60 * 1000; // 24 hours in milliseconds

// Detect platform
const getPlatform = () => {
  const ua = navigator.userAgent || '';
  const isIOS = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);
  const isMobile = isIOS || isAndroid || /webOS|BlackBerry|Opera Mini|IEMobile/.test(ua);
  if (isIOS) return 'ios';
  if (isMobile) return 'other-mobile';
  return 'desktop';
};

const IOSAppBanner = () => {
  const { user, isAuthenticated } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [platform] = useState(() => getPlatform());

  // Get the appropriate storage key based on user status
  const getStorageKey = () => {
    if (isAuthenticated && user?.id) {
      return `ios_banner_dismissed_${user.id}`;
    }
    return 'ios_banner_dismissed_guest';
  };

  useEffect(() => {
    // Get storage key based on current auth state
    const storageKey = getStorageKey();
    
    // Check both localStorage and cookie for dismissal time
    const dismissedAtLocalStorage = localStorage.getItem(storageKey);
    const dismissedAtCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${storageKey}=`))
      ?.split('=')[1];
    
    // Use the most recent dismissal time from either source
    const dismissedAt = dismissedAtLocalStorage || dismissedAtCookie;
    
    // Check if banner should be shown (cooldown has passed or never dismissed)
    let shouldShow = true;
    
    if (dismissedAt) {
      const dismissedTimestamp = parseInt(dismissedAt, 10);
      const now = Date.now();
      const timeSinceDismissal = now - dismissedTimestamp;

      // Only show if cooldown period (24 hours) has passed
      shouldShow = timeSinceDismissal >= COOLDOWN_MS;
    }

    // Check cooldown BEFORE setting isMounted or isVisible
    if (!shouldShow) {
      logger.log('📱 Banner still in cooldown — skipping render.');
      return;
    }

    const timer = setTimeout(() => {
      setIsMounted(true);
      // Small delay to ensure the element is rendered with initial position before animating
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
          const userContext = isAuthenticated && user?.id ? `user ${user.id}` : 'guest';
          logger.log(`📱 iOS App Banner: Showing banner to ${userContext}`);
        });
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [isAuthenticated, user?.id]); // Re-check when auth state changes

  // Auto-dismiss after 10 seconds
  useEffect(() => {
    if (!isVisible) return;

    const autoDismissTimer = setTimeout(() => {
      handleDismiss();
    }, 10000); // 10 seconds

    return () => clearTimeout(autoDismissTimer);
  }, [isVisible]);

  const handleDismiss = () => {
    const storageKey = getStorageKey();
    const now = Date.now();
    
    // Store dismissal timestamp in localStorage
    localStorage.setItem(storageKey, now.toString());
    
    // Also store in cookie for Safari iOS reliability
    document.cookie = `${storageKey}=${now}; max-age=${COOLDOWN_HOURS * 3600}; path=/`;
    
    setIsVisible(false);
    const userContext = isAuthenticated && user?.id ? `user ${user.id}` : 'guest';
    logger.log(`📱 iOS App Banner: ${userContext} dismissed banner. Will show again after ${COOLDOWN_HOURS} hours.`);
  };

  const handleOpenApp = () => {
    logger.log('📱 iOS App Banner: User clicked "Open App" button');
    window.open('https://apps.apple.com/us/app/pixley-personalized-cartoons/id6751121342', '_blank');
  };

  // Don't render at all until it's time to show
  if (!isMounted) {
    return null;
  }

  const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

  // Shared chrome — used by both the iOS prompt and the desktop-suggest prompt.
  const renderBanner = ({ title, subtitle, body, primaryLabel, onPrimary, primaryIcon }) => (
    <div
      className={`fixed bottom-6 right-6 z-50 w-full max-w-sm transition-all duration-500 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    >
      <div
        className="relative overflow-hidden"
        style={{
          background: '#FFFFFF',
          border: '2px solid rgba(11,26,51,0.06)',
          borderRadius: 22,
          boxShadow: '0px 5px 0px rgba(11,26,51,0.10), 0px 18px 36px rgba(11,26,51,0.18)',
          fontFamily: "'Inter', 'Fredoka', sans-serif",
        }}
      >
        {/* Soft blue accent blob in the corner */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 160,
            height: 160,
            borderRadius: '50%',
            background: 'rgba(0,101,248,0.10)',
            filter: 'blur(20px)',
          }}
        />

        <div className="relative p-5">
          <div className="flex items-start gap-3">
            <div className="relative flex-shrink-0">
              <img
                src="/assets/pixley-panda-nobg.png"
                alt="Pixley"
                style={{
                  height: 52,
                  width: 52,
                  borderRadius: 14,
                  border: '2px solid rgba(11,26,51,0.06)',
                  boxShadow: '0px 3px 0px rgba(11,26,51,0.08)',
                  background: '#FFFFFF',
                }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p
                style={{
                  ...FREDOKA,
                  color: '#0B1A33',
                  fontWeight: 700,
                  fontSize: 16,
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                  lineHeight: 1.1,
                  margin: 0,
                }}
              >
                {title}
              </p>
              <p
                style={{
                  ...FREDOKA,
                  color: '#5A6B85',
                  fontSize: 12,
                  fontWeight: 500,
                  margin: '4px 0 0',
                }}
              >
                {subtitle}
              </p>
            </div>
            <button
              onClick={handleDismiss}
              aria-label="Dismiss banner"
              className="transition active:scale-[0.94]"
              style={{
                width: 32,
                height: 32,
                background: '#FFFFFF',
                color: '#5A6B85',
                border: '2px solid rgba(11,26,51,0.08)',
                borderRadius: 10,
                boxShadow: '0px 2px 0px rgba(11,26,51,0.06)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <p
            style={{
              ...FREDOKA,
              color: '#334155',
              fontSize: 14,
              lineHeight: 1.5,
              margin: '14px 0 16px',
            }}
          >
            {body}
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={onPrimary}
              className="flex-1 inline-flex items-center justify-center gap-2 transition active:scale-[0.98]"
              style={{
                ...FREDOKA,
                background: '#0065F8',
                color: '#FFFFFF',
                fontWeight: 700,
                fontSize: 14,
                border: 'none',
                borderRadius: 999,
                padding: '12px 18px',
                boxShadow: '0px 4px 0px #073B8E, 0px 10px 22px rgba(0,101,248,0.22)',
                cursor: 'pointer',
              }}
            >
              {primaryIcon}
              {primaryLabel}
            </button>
            <button
              onClick={handleDismiss}
              className="transition active:scale-[0.98]"
              style={{
                ...FREDOKA,
                background: '#FFFFFF',
                color: '#5A6B85',
                fontWeight: 700,
                fontSize: 13,
                border: '2px solid rgba(11,26,51,0.08)',
                borderRadius: 999,
                padding: '12px 18px',
                boxShadow: '0px 2px 0px rgba(11,26,51,0.04)',
                cursor: 'pointer',
              }}
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Non-iOS mobile: "better on desktop" banner
  if (platform === 'other-mobile') {
    return renderBanner({
      title: 'Try Pixley on Desktop',
      subtitle: 'Get the full experience',
      body: (
        <>
          Pixley works best on desktop or our iOS app. Visit{' '}
          <span style={{ ...FREDOKA, color: '#0065F8', fontWeight: 800 }}>pixleyai.com</span>{' '}
          on your computer for the best experience!
        </>
      ),
      primaryLabel: 'Got it',
      onPrimary: handleDismiss,
      primaryIcon: (
        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      ),
    });
  }

  // iOS + Desktop: iOS app banner
  return renderBanner({
    title: 'Pixley on iOS',
    subtitle: 'Keep Pixley in your pocket',
    body:
      'Download our iOS app for push notifications, faster playback, and easy sharing.',
    primaryLabel: 'Open App Store',
    onPrimary: handleOpenApp,
    primaryIcon: (
      <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
      </svg>
    ),
  });
};

export default IOSAppBanner;
